import {IRoutesProvider} from 'Shared/Routes';
import { lazy } from 'solid-js';
import { sharedRoutes } from 'Shared/routes/SharedRoutes';


export class VenueRoutes implements IRoutesProvider
{
	constructor(
		private customPages
	)
	{ }

	routes()
	{
//TODO add redirect route for all trailing /s here. Currently a hack is fixed in Server.resolveRoute()

		return ([
			...this.frontend(),
			...this.backend(),

			//TODO want a frontend page not found page. Doing this prevents '/logout' (and probably other routes from reaching the server router though).
			//     Could possibly render and call the page not found page directly from the Server.ts file
			// '/*':	[new FrontendPageNotFound(this.inBrowser,this.urls,this.times),() => ({}) ],
		]);
	}

	frontend()
	{
		return ([
			{
				path: '/staffResources',
				component: lazy(() => import('Shared/frontend/staffResources/StaffResourcePage')),
			},
			{
				path: '/',
				component: lazy(() => import('Shared/frontend/layouts/StandardLayout')),
				children: this.frontendContent()
			},
		]); 
	}

	frontendContent()
	{
		let ret = [
			{
				path: '/menu',
				component: lazy(() => import('Shared/frontend/menuPage/MenuPage')),
			},
			{
				path: '/event/:slug',
				component: lazy(() => import('Shared/frontend/eventPage/EventPage')),
			},
			{
				path: '/event/:slug/artist',
				component: lazy(() => import('Shared/frontend/eventPage/ArtistAgreementPage')),
			},
		];

		for (const page of this.customPages) {
			if (page.frontend?.route == null)
				throw new Error(`Custom page '${page.key}' is missing frontend.route`);
			ret = [...ret, {
					path: page.frontend.route,
					component: lazy(() => import('Shared/frontend/CustomPage'))
				}
			];
		}

		ret = [
			...ret,
			//TODO hack for the drummer, need generalisation
			{
				path: '/drink',
				component: lazy(() => import('Shared/frontend/menuPage/MenuPage')),
			},
			{
				path: '/dine',
				component: lazy(() => import('Shared/frontend/menuPage/MenuPage')),
			},
		];

		return ret;
	}

	/*
		WARNING
		Careful if leaving a backend page to jump to the frontend or artist GUI. 
		PosterRenderer could still be active and currently this action is NOT accounted for.
	*/
	backend()
	{
		let wrappedRoutes = [
			{
				path: '/',
				component: lazy(() => import('Shared/backend/DashboardPage')),
			},
			{
				path: '/user',
				component: lazy(() => import('Shared/backend/UserPage')),
			},

			...this.backendEvent(),

			...this.backendMenus(),

			...this.backendSpecials(),

			{
				path: [
					'/info',
					'/info/:tab',
				],
				component: lazy(() => import('Shared/backend/infoPage/InfoPage')),
			},
			{
				path: '/posters',
				component: lazy(() => import('Shared/backend/postersPage/PostersPage')),
			},
			{
				path: '/posterTheme/:id',
				component: lazy(() => import('Shared/backend/posterPage/PosterThemePage')),
			},
			{
				path: '/poster/:id',
				component: lazy(() => import('Shared/backend/posterPage/PosterDesignPage')),
			},
			{
				path: '/dashboard',
				component: lazy(() => import('Shared/backend/dashboard/DashboardPage')),
			},
			{
				path: '/staffResources',
				component: lazy(() => import('Shared/backend/staffResources/StaffResourcesPage')),
			},
			{
				path: '/staffResource/:id',
				component: lazy(() => import('Shared/backend/staffResources/StaffResourcePage')),
			},
			{
				path: '/nav',
				component: lazy(() => import('Shared/backend/navSettingsPage/NavSettingsPage')),
			},
			{
				path: '/settings',
				component: lazy(() => import('Shared/backend/settingsPage/SettingsPage')),
			},
			{
				path: '/style-builder',
				component: lazy(() => import('Shared/backend/styleBuilderPage/StyleBuilderPage')),
			},
			{
				path: '/testFields',
				component: lazy(() => import('Shared/backend/testFieldsPage/TestFieldsPage')),
			},
			{
				//TODO return 404
				path: '*',
				component: lazy(() => import('Shared/backend/PageNotFoundPage')),
			}
		];

		for (const page of this.customPages) {
			if (page.backend?.route == null)
				throw new Error(`Custom page '${page.key}' is missing backend.route`);
			wrappedRoutes = [...wrappedRoutes, {
					path: page.backend.route,
					component: lazy(() => import('Shared/backend/CustomPage'))
				}
			];
		}

		return ([
			{
				path: '/login',
				component: lazy(() => import('Shared/users/LoginPage')),
			},
			...sharedRoutes(),
			{
				path: '/admin',
				component: lazy(() => import('Shared/backend/BackendWrap')),
				children: wrappedRoutes
			}
		]); 
	}

	backendSpecials()
	{
		return [
			{
				path: '/specials',
				component: lazy(() => import('Shared/backend/specials/SpecialsPage')),
			},
			{
				path: '/special/:id',
				component: lazy(() => import('Shared/backend/specials/SpecialPage')),
			},
		];
	}

	backendEvent()
	{
		return [
			{
				path: '/events/orphaned-agreements',
				component: lazy(() => import('Shared/backend/eventsPage/OrphanedAgreementsPage')),
			},
			{
				path: '/events',
				component: lazy(() => import('Shared/backend/eventsPage/EventsPage')),
			},
			{
				path: [
					'/event/:slug',
					'/event/:slug/:topTab',
					'/event/:slug/:topTab/:secondTab',
					'/event/:slug/:topTab/:secondTab/:thirdTab'
				],
				component: lazy(() => import('Shared/backend/eventPage/EventPage')),
			}
		];
	}

	backendMenus()
	{
		return [
			{
				path: '/listMenu/:slug',
				component: lazy(() => import('Shared/backend/menu/ListMenuPage')),
			},
			{
				path: '/menus',
				component: lazy(() => import('Shared/backend/menu/MenusPage')),
			},
			{
				path: '/menu-designer',
				component: lazy(() => import('Shared/backend/menu/MenuDesignerPage')),
			},
			{
				path: [
					'/menus/:slug',
					'/menus/:slug/*sectionIds',
				],
				component: lazy(() => import('Shared/backend/menu/SectionPage')),
			},
			{
				path: '/menu-item/:slug/*sectionIds',
				component: lazy(() => import('Shared/backend/menu/MenuItemPage')),
			},
		];
	}
}

export function extractMenuSectionIds(sectionsIds?:string)
{
	if (!sectionsIds)
		return [];

	const parts = [];
	for (const bit of sectionsIds.split('/')) {
		const segments = bit.split('-');
		parts.push({name:segments[0],index:Number(segments[1])});
	}
	return parts;
}

